"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.installAppBaseBindings = installAppBaseBindings;
const app_base_binding_read_1 = require("./app-base-binding-read");
/** Embedded as a function, with dependencies passed explicitly. No polling. */
function installAppBaseBindings(api, document, runtime, project, urlIssue, budgets, docHtml, markdown, assets, readDatasets = app_base_binding_read_1.readAppBaseBindingDatasets, 
/** Row choice of pages with `rowSelection`. Passed in, never defaulted:
 * this function runs in the frame as source text, where a module default
 * does not exist. */
resolveFixedRow, 
/** The literal keys of lists that only show the rows of another row, which
 * then read like literal lists. Passed in like `resolveFixedRow`. */
rowFilterKeys, readCounts = app_base_binding_read_1.readAppBaseBindingCounts, literalFilterQuery = app_base_binding_read_1.appBaseBindingLiteralFilterQuery, readLiteralLists = app_base_binding_read_1.readAppBaseBindingLiteralLists, windowRows = app_base_binding_read_1.APP_BASE_BINDING_WINDOW_ROWS, selectListFilters = app_base_binding_read_1.selectAppBaseBindingListFilters, 
/** Places long lists by their rows (databaseRowWindow); passed in like the rest. */
rows) {
    const root = document.querySelector('html[data-sl-binding-ssr="1"]');
    if (!root)
        return { dispose: () => { }, rendered: Promise.resolve() };
    const selected = root.getAttribute('data-sl-binding-selected');
    const route = root.getAttribute('data-sl-binding-route') ?? '/';
    const dynamicSource = root.getAttribute('data-sl-binding-dynamic-source');
    const dynamicColumn = root.getAttribute('data-sl-binding-dynamic-column');
    const dynamicValue = root.getAttribute('data-sl-binding-dynamic-value');
    const dynamicOwner = root.getAttribute('data-sl-binding-dynamic-owner');
    const dynamicSelection = dynamicSource && dynamicOwner === 'me'
        ? { sourceKey: dynamicSource, owner: 'me' }
        : dynamicSource && dynamicColumn && dynamicValue
            ? {
                sourceKey: dynamicSource,
                columnId: dynamicColumn,
                value: dynamicValue,
            }
            : undefined;
    const fixedRow = root.getAttribute('data-sl-binding-fixed-row');
    const fixedSelection = fixedRow
        ? {
            ...JSON.parse(fixedRow),
            locale: root.getAttribute('data-sl-binding-locale') ?? '',
        }
        : undefined;
    // A file without an address leaves its element empty, never the page; the
    // render reports the first one after it is done.
    let assetErrors = [];
    const view = document.defaultView;
    // Where the window of each listed source begins, and the data drawn last:
    // scrolling draws the lists again from it, a list beyond it reads anew.
    const offsets = Object.create(null);
    let shown;
    let frame;
    const redraw = () => {
        if (frame !== undefined || !view)
            return;
        frame = view.requestAnimationFrame(() => {
            frame = undefined;
            if (shown && !disposed)
                engine.render(shown);
        });
    };
    /** The nearest ancestor that scrolls its rows, or none (the page). */
    const scroller = (start) => {
        for (let at = start.parentElement; at; at = at.parentElement) {
            const overflow = view.getComputedStyle(at).overflowY;
            if ((overflow === 'auto' || overflow === 'scroll') &&
                at.scrollHeight > at.clientHeight)
                return at;
        }
        return null;
    };
    const engine = runtime(root, {
        urlIssue,
        budgets,
        docHtml,
        markdown,
        routeBasePath: root.getAttribute('data-sl-binding-base') ?? '',
        routeLocale: root.getAttribute('data-sl-binding-route-locale') ?? undefined,
        assetUrl: assets
            ? (value, sourceKey, rowId, columnId) => {
                try {
                    if (assets.resolve)
                        return assets.resolve(value, sourceKey, rowId, columnId);
                    const image = assets.publishedImage(value, root.getAttribute('data-sl-binding-image-base') ?? undefined);
                    if (image)
                        return image;
                    const file = assets.normalize(value)[0];
                    if (!file)
                        return null;
                    if (api.getRouteContext().runtimeMode === 'internal_live')
                        return api.resolveAttachmentUrl(file, {
                            route,
                            sourceKey,
                            rowId,
                            columnId,
                        });
                    return assets.path({
                        basePath: root.getAttribute('data-sl-binding-base') ?? '',
                        route,
                        sourceKey,
                        rowId,
                        columnId,
                        blobId: file.blobId,
                        token: document
                            .querySelector('meta[name="streamlain-custom-data-token"]')
                            ?.getAttribute('content'),
                    });
                }
                catch (error) {
                    assetErrors.push(error);
                    return null;
                }
            }
            : undefined,
        sameAsset: assets
            ? (current, next) => assets.same(current, next, document.baseURI)
            : undefined,
        selectedRows: selected ? JSON.parse(selected) : {},
        docLinks: JSON.parse(root.getAttribute('data-sl-binding-doc-links') ?? '{}'),
        rowWindow: rows && view
            ? {
                ...rows,
                viewport: start => {
                    const top = start.getBoundingClientRect().top;
                    const box = scroller(start)?.getBoundingClientRect();
                    return {
                        top: Math.max(box?.top ?? 0, 0) - top,
                        bottom: Math.min(box?.bottom ?? view.innerHeight, view.innerHeight) -
                            top,
                    };
                },
                box: node => {
                    const { top, height } = node.getBoundingClientRect();
                    return { top, height };
                },
                want: (source, index) => {
                    if (offsets[source] === index)
                        return;
                    offsets[source] = index;
                    void refresh();
                },
                changed: redraw,
            }
            : undefined,
    });
    const sources = engine.inspect(false);
    if (!sources.length)
        return { dispose: () => { }, rendered: Promise.resolve() };
    let markRendered = () => { };
    const rendered = new Promise(resolve => {
        markRendered = resolve;
    });
    const listFilters = engine.inspectListFilters(false);
    const counts = engine.inspectCounts(false);
    let disposed = false;
    let running = false;
    let dirty = false;
    let revocationRevision = 0;
    let pending;
    const clear = () => engine.render(Object.fromEntries(sources.map(source => [
        source,
        { status: 'unavailable', columns: [], rows: [] },
    ])));
    const refresh = async () => {
        if (disposed)
            return;
        dirty = true;
        if (running)
            return;
        running = true;
        try {
            do {
                dirty = false;
                const admittedRevision = revocationRevision;
                const request = new AbortController();
                pending = request;
                let timer;
                let data;
                try {
                    data = await Promise.race([
                        readDatasets((sourceKey, query) => api.querySource(sourceKey, query), sources, {
                            signal: request.signal,
                            route,
                            selectedRows: selected ? JSON.parse(selected) : {},
                            dynamicSelection,
                            fixedSelection,
                            listFilters,
                            offsets,
                        }, resolveFixedRow, rowFilterKeys, literalFilterQuery, readLiteralLists, windowRows, selectListFilters).then(async (data) => {
                            await readCounts((sourceKey, query) => api.querySource(sourceKey, query), counts, data, literalFilterQuery);
                            return data;
                        }),
                        new Promise((_resolve, reject) => {
                            timer = setTimeout(() => {
                                request.abort();
                                reject(new Error('Binding dataset request timed out (limit binding.readMs)'));
                            }, budgets.readMs);
                        }),
                    ]);
                }
                finally {
                    clearTimeout(timer);
                    pending = undefined;
                }
                if (disposed)
                    return;
                // A request admitted before a revocation cannot restore cleared data.
                // The queued refresh rechecks access; there is no automatic retry.
                if (admittedRevision !== revocationRevision)
                    continue;
                // querySource has one canonical cell contract in every delivery mode:
                // each cell is { value }. Snapshot materialization is an SSR concern.
                assetErrors = [];
                try {
                    shown = project(data, sources, 'internal_live');
                    engine.render(shown);
                }
                finally {
                    // What the render took, also one a budget stopped: the StreamKit bar
                    // shows it against the limits. Spelled like
                    // APP_BASE_BINDING_USAGE_EVENT; this function runs as source text.
                    const view = document.defaultView;
                    if (view)
                        view.dispatchEvent(new view.CustomEvent('sl-binding-usage', {
                            detail: { route, ...engine.usage() },
                        }));
                }
            } while (dirty && !disposed);
            if (assetErrors.length)
                api.reportError(assetErrors[0]);
            else
                api.reportReady();
        }
        catch (error) {
            if (!disposed) {
                try {
                    clear();
                }
                catch (clearError) {
                    api.reportError(clearError);
                }
                api.reportError(error);
            }
        }
        finally {
            running = false;
            markRendered();
        }
    };
    api.beginLoading({ stage: 'data_loading' });
    const unsubscribes = sources.map(sourceKey => api.watchSource(sourceKey, event => {
        if (event?.unavailableSources?.length) {
            revocationRevision++;
            clear();
        }
        void refresh();
    }));
    // A scroll anywhere in the page (captured) or a new size draws the lists
    // for their new viewport.
    view?.addEventListener('scroll', redraw, { capture: true, passive: true });
    view?.addEventListener('resize', redraw, { passive: true });
    const dispose = () => {
        disposed = true;
        pending?.abort();
        view?.removeEventListener('scroll', redraw, { capture: true });
        view?.removeEventListener('resize', redraw);
        if (frame !== undefined)
            view?.cancelAnimationFrame(frame);
        for (const unsubscribe of unsubscribes)
            unsubscribe();
        document.defaultView?.removeEventListener('pagehide', dispose);
        markRendered();
    };
    document.defaultView?.addEventListener('pagehide', dispose, { once: true });
    void refresh();
    return { dispose, rendered };
}
